import { lazy, Suspense } from "react";
import { LazyMotion, domAnimation } from "framer-motion";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import Index from "./pages/Index";
import ScrollToTop from "./components/ScrollToTop";
import ScrollTopButton from "./components/ScrollTopButton";
import ReadingProgress from "./components/ReadingProgress";
import ChatWidget from "./components/ChatWidget";
import ChunkErrorBoundary from "./components/ChunkErrorBoundary";
import { QuoteFormProvider } from "./components/QuoteFormContext";

// Homepage is eager (it is the LCP-critical entry). Secondary routes are
// code-split so their JS is only fetched on navigation, keeping the initial
// bundle small.
const Services = lazy(() => import("./pages/Services"));
const ServiceDetail = lazy(() => import("./pages/ServiceDetail"));
const Gallery = lazy(() => import("./pages/Gallery"));
const Blog = lazy(() => import("./pages/Blog"));
const BlogPost = lazy(() => import("./pages/BlogPost"));
const About = lazy(() => import("./pages/About"));
const Locations = lazy(() => import("./pages/Locations"));
const LocationDetail = lazy(() => import("./pages/LocationDetail"));
const NotFound = lazy(() => import("./pages/NotFound"));

const PageLoader = () => (
  <div className="flex min-h-[60vh] items-center justify-center" aria-busy="true" aria-live="polite">
    <span className="sr-only">Loading…</span>
    <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
  </div>
);

const App = () => (
  // LazyMotion + the `m` components (instead of `motion`) load only the DOM
  // animation feature set framer-motion needs here, trimming the bundle.
  <LazyMotion features={domAnimation} strict>
    <QuoteFormProvider>
      <BrowserRouter>
        <ScrollToTop />
        <ReadingProgress />
        <ChunkErrorBoundary>
          <Suspense fallback={<PageLoader />}>
            <Routes>
              <Route path="/" element={<Index />} />
              <Route path="/services" element={<Services />} />
              <Route path="/services/:slug" element={<ServiceDetail />} />
              <Route path="/gallery" element={<Gallery />} />
              <Route path="/blog" element={<Blog />} />
              <Route path="/blog/:slug" element={<BlogPost />} />
              <Route path="/about" element={<About />} />
              <Route path="/locations" element={<Locations />} />
              <Route path="/locations/:slug" element={<LocationDetail />} />
              {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
        </ChunkErrorBoundary>
        <ScrollTopButton />
        <ChatWidget />
      </BrowserRouter>
    </QuoteFormProvider>
  </LazyMotion>
);

export default App;
